import React, { Component } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import * as Immutable from "immutable";
import { AutoSizer, Grid, List, WindowScroller } from "react-virtualized";
import "react-virtualized/styles.css";
import debounce from "lodash.debounce";
import PropTypes from "prop-types";

import { CPage, H3 } from "@appcues/component-library";

import StepListItem from "components/StepIndex/StepView/StepListItem";
import StepCardItem from "components/StepIndex/StepView/StepCardItem";
import ProductLedExampleFlows from "components/StepIndex/ProductLedExampleFlows";
import withTiming from "components/Common/withTiming";
import megaphone from "assets/images/announcement-single.png";
import StepImage from "components/Common/StepImage";
import OpenInBuilderModal from "components/OpenInBuilder/OpenInBuilderModal";

import { FLOW_LAYOUT } from "constants/view";
import { STEP_TYPE_IDS } from "constants/stepTypes";
import { getTagsForStep, getStepType } from "helpers/steps";
import {
  getCreatedByUserFromStep,
  getUpdatedByUserFromStep,
  getGoalFromRule,
  getTargetedSegmentFromConditions
} from "helpers/view";
import { getVersionIdFromStep } from "utils/steps";
import { navigate } from "actions/routing";
import { handleFlowActions } from "actions/view";
import { selectAccountRules } from "reducers/account/rules";

const LIST_ROW_HEIGHT = 136;
const GRID_CELL_HEIGHT = 300;

export class StepView extends Component {
  static propTypes = {
    className: PropTypes.string,
    showPLGFlows: PropTypes.bool
  };

  static defaultProps = {
    showPLGFlows: true
  };

  state = {
    numColumns: 3,
    isOpenInBuilderModalOpen: false,
    selectedFlowUrl: null,
    selectedFlowId: null
  };

  componentDidMount = () => {
    this.determineColumnCount();
    window.addEventListener("resize", this.debouncedDetermineColumnCount);
  };

  componentWillUnmount = () => {
    window.removeEventListener("resize", this.debouncedDetermineColumnCount);
  };

  determineColumnCount = () => {
    let numColumns;

    if (window.innerWidth < 700) {
      numColumns = 1;
    } else if (window.innerWidth < 1200) {
      numColumns = 2;
    } else {
      numColumns = 3;
    }

    this.setState({ numColumns });
  };

  renderItemIcon = ({ step, stepImageHeight }) => {
    const stepType = getStepType(step);
    switch (stepType) {
      case STEP_TYPE_IDS.ANNOUNCEMENT:
        return <img alt="Announcement" src={megaphone} />;

      default: {
        const stepVersionId = getVersionIdFromStep(step);
        return (
          <StepImage
            heightOverride={stepImageHeight}
            stepId={step.id}
            versionId={stepVersionId || "latest"}
          />
        );
      }
    }
  };

  debouncedDetermineColumnCount = debounce(this.determineColumnCount, 100);

  showOpenInBuilderModal = url => {
    this.setState({ isOpenInBuilderModalOpen: true, selectedFlowUrl: url });
  };

  hideOpenInBuilderModal = () => {
    this.setState({ isOpenInBuilderModalOpen: false });
  };

  onDropdownItemSelected = actionData => {
    const { handleFlowActions } = this.props;
    const {
      stepType,
      stepId,
      action: actionType,
      handler: actionHandler
    } = actionData;
    const data = {
      content_type: stepType,
      content_id: stepId,
      showOpenInBuilderModal: this.showOpenInBuilderModal
    };

    this.setState({ selectedFlowId: stepId });

    handleFlowActions(actionType, actionHandler, data);
  };

  goToAnalyticsPage = ({ id }) => {
    const { navigate } = this.props;
    navigate(`/steps/${id}/analytics`);
  };

  getDefaultClickAction = step => {
    this.goToAnalyticsPage(step);
  };

  // ignore provided key and use stepId because the provided key by `react-virtualized`
  // uses the index in the data array, which is bad react practice
  listViewRowRenderer = ({ index, key: omit, style }) => {
    const {
      conditions,
      goals,
      meta,
      rules,
      segments,
      sortedSteps,
      analytics,
      tags,
      users
    } = this.props;

    const step = sortedSteps[index];

    const stepTags = Immutable.Set(getTagsForStep(step, tags));
    const stepRule = rules[step.id];
    const stepGoal = getGoalFromRule(stepRule, goals);

    const stepConditions = conditions[step.id];
    const targetedSegment = getTargetedSegmentFromConditions(
      stepConditions,
      segments
    );

    const createdUser = getCreatedByUserFromStep(step, users);
    const updatedUser = getUpdatedByUserFromStep(step, users);

    const analyticsData = analytics && analytics.data ? analytics.data : {};
    const stepAnalytics = Immutable.Map(analyticsData[step.id]);
    const tagsMap = Immutable.Map(tags);
    return (
      <div key={step.id} style={style}>
        <StepListItem
          step={step}
          stepTags={stepTags}
          stepAnalytics={stepAnalytics}
          stepGoal={stepGoal}
          tags={tagsMap}
          createdAt={createdUser && createdUser.createdDate}
          updatedAt={updatedUser && updatedUser.updatedDate}
          targetedSegmentName={targetedSegment && targetedSegment.name}
          isInstalled={meta.isInstalled}
          onSelectDropdownOption={this.onDropdownItemSelected}
          onClickFlowItem={this.getDefaultClickAction}
          renderItemIcon={this.renderItemIcon}
        />
      </div>
    );
  };

  // ignore provided key and use stepId because the provided key by `react-virtualized`
  // uses the index in the data array, which is bad react practice
  gridCellRenderer = ({ columnIndex, key: omit, rowIndex, style }) => {
    const { meta, sortedSteps, users } = this.props;
    const { numColumns } = this.state;

    const step = sortedSteps[rowIndex * numColumns + columnIndex];
    if (!step) {
      return undefined;
    }

    const createdUser = getCreatedByUserFromStep(step, users);
    const updatedUser = getUpdatedByUserFromStep(step, users);

    return (
      <div key={step.id} style={style}>
        <StepCardItem
          stepId={step.id}
          step={step}
          createdDate={createdUser.createdDate}
          updatedDate={updatedUser.updatedDate}
          isInstalled={meta.isInstalled}
          onSelectDropdownOption={this.onDropdownItemSelected}
          onClickFlowItem={this.getDefaultClickAction}
          renderItemIcon={this.renderItemIcon}
        />
      </div>
    );
  };

  render() {
    const {
      className,
      showPLGFlows,
      flowLayout,
      stepsFromSearch,
      sortedSteps,
      areProductLedFlowsMinimized
    } = this.props;
    const {
      numColumns,
      isOpenInBuilderModalOpen,
      selectedFlowUrl,
      selectedFlowId
    } = this.state;
    return (
      <CPage.Container className={className}>
        {showPLGFlows && (
          <ProductLedExampleFlows
            areProductLedFlowsMinimized={areProductLedFlowsMinimized}
            hasFlows
          />
        )}
        {isOpenInBuilderModalOpen && (
          <OpenInBuilderModal
            url={selectedFlowUrl}
            onClose={this.hideOpenInBuilderModal}
            stepGroupId={selectedFlowId}
          />
        )}
        <StyledFlowListContainer>
          {!stepsFromSearch.length ? (
            <H3>No flows match your filters.</H3>
          ) : (
            <WindowScroller>
              {({ height, isScrolling, onChildScroll, scrollTop }) => (
                <AutoSizer>
                  {({ width }) =>
                    flowLayout === FLOW_LAYOUT.LIST ? (
                      <List
                        autoHeight
                        height={height}
                        rowCount={sortedSteps.length}
                        rowHeight={LIST_ROW_HEIGHT}
                        rowRenderer={this.listViewRowRenderer}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                        scrollTop={scrollTop}
                        width={width}
                        {...this.props}
                      />
                    ) : (
                      <Grid
                        autoHeight
                        cellRenderer={this.gridCellRenderer}
                        columnWidth={width / numColumns}
                        columnCount={numColumns}
                        height={height}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                        rowCount={Math.ceil(sortedSteps.length / numColumns)}
                        rowHeight={GRID_CELL_HEIGHT}
                        scrollTop={scrollTop}
                        width={width}
                        {...this.props}
                      />
                    )
                  }
                </AutoSizer>
              )}
            </WindowScroller>
          )}
        </StyledFlowListContainer>
      </CPage.Container>
    );
  }
}

const mapStateToProps = state => ({
  // only used to get goalId, which is included in summary
  rules: selectAccountRules(state)
});

const mapDispatchToProps = { navigate, handleFlowActions };

const StyledFlowListContainer = styled.div`
  padding-bottom: 80px;
  margin-right: -16px; /* Compensate for grid */

  > div:first-child {
    /* react-virtualized adds a "height: 0" to the container that isn't
       actually necessary, but causes problems with responsiveness */
    height: auto !important;
  }

  .ReactVirtualized__Grid {
    outline: none;
    overflow: visible !important;

    margin-bottom: 8rem; /* Allow for some scrolling tolerance at the bottom. */
  }

  .ReactVirtualized__Grid__innerScrollContainer {
    overflow: visible !important;
  }
`;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTiming(StepView, "StepView"));
